import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { useDocumentPreviewUrl } from '@/features/finance/api/useFinance'
import { decimalPlaces } from '@/features/finance/invoice'
import { useFormat } from '@/i18n/useFormat'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseDocument } from '@/types/domain'

/* The document viewer, in its two modes — and the switch between them IS the
 * state machine `00-decisions.md` §1 describes.
 *
 * A document is DRAFT, then RENDERED, then ISSUED, and the first two of those
 * are two different things wearing the word "preview":
 *
 *   · the DRAFT preview is a layout this client draws from the fields it
 *     already holds. No Chromium, no round trip, no archive row. It exists
 *     because the loop a person is actually in while building a document is
 *     type a line → see it → fix it (UX-DR-15), and putting a headless render
 *     in that loop makes every keystroke cost a page render;
 *
 *   · the RENDERED preview is the archived PDF itself, streamed from object
 *     storage behind a signed URL. `document.Handlers.Preview` states the rule
 *     it exists to honour: "the preview and the issued artifact are the SAME
 *     bytes" — a preview rendered by a different path is a preview that can
 *     lie, and somebody who only ever saw an HTML approximation has not seen
 *     the document they are about to issue (FR-63).
 *
 * ONE component rather than two, because the two modes are not two screens:
 * they are one screen answering "is what I am looking at the document, or a
 * sketch of it", and the whole point is that the answer changes underneath the
 * reader as they generate and then amend. Split into two components and the
 * caller becomes the thing that decides which is current — which is a second
 * copy of the rule below, in a file that has no reason to know it.
 *
 * ── The one signal ─────────────────────────────────────────────────────────
 *
 * `record.documentId` decides the mode and nothing else does. The server clears
 * that column on EVERY draft write (`TouchInvoice`, `UpdateInvoiceHeader`,
 * `TouchDebitNote`, `UpdateDebitNote`), so its presence means "a CURRENT render
 * exists" rather than "a render once happened". A client-side "dirty since
 * render" flag would be a second answer to a question the server already
 * answers, and the two disagree the first time somebody edits the note in
 * another tab.
 *
 * ── What this component must never grow ────────────────────────────────────
 *
 * NO Print, NO Download, NO "Open", no anchor, no wording implying finality —
 * on the DRAFT side. This is stated here because it is the constraint a
 * well-meaning change breaks first: a draft layout that looks like a document
 * invites a Print button, and the moment one exists somebody sends a client a
 * page this client drew instead of the artifact the server archived. The two
 * are allowed to differ — the draft deliberately does not reproduce the PDF's
 * typography — and that is exactly why only one of them may leave the company.
 */

/** One charge as the draft layout prints it.
 *
 *  Structurally satisfied by both `InvoiceLine` and `DebitNoteLine` as served,
 *  so a caller passes the detail query's own array straight through: `note` is
 *  optional because an invoice line has no qualifier field at all while a debit
 *  note's is always present (`''` when empty), and every figure is a decimal
 *  STRING because every figure in this system is (AD-9).
 *
 *  `amount` is CARRIED, never derived. It is unit price × quantity computed
 *  once on the server, and a layout that multiplied the two itself would be a
 *  second implementation of the document's arithmetic — the first time it
 *  disagreed, the reader would be checking a total that the PDF does not
 *  contain. Recomputing a served figure is what AD-9 forbids; comparing or
 *  printing one is not. */
export interface DocumentPreviewLine {
  id: string
  description: string
  /** The small qualifier under the description — "4 Containers (Open-top)".
   *  Answers "why this quantity", which is the first thing a client asks about
   *  a charge they did not expect. */
  note?: string
  unitPrice: string
  quantity: string
  amount: string
}

export interface DocumentPreviewProps {
  /** The record itself, as both Documents surfaces already hold it. */
  record: CaseDocument
  /** The draft's charges, as the detail query serves them. Empty is legitimate
   *  — an invoice list row carries no lines at all — and renders as a header
   *  and a total rather than as an error. */
  lines: DocumentPreviewLine[]
  /** The issuing entity, which the SCREEN knows and the record does not.
   *
   *  `CaseDocument.entityName` is `''` for both kinds: neither `InvoiceSummary`
   *  nor `DebitNote` serves an entity NAME — they serve what will PRINT from it
   *  (payment terms, bank details) and nothing else. So it is passed down from
   *  the case or the Money page, which resolved it for their own headers, rather
   *  than left as a blank on the paper. */
  entityName?: string
  /** The currency the figures are priced in, stated once for the whole table.
   *  Defaults to the record's own — one document, one currency, because an
   *  amount column in two has no total anybody can read. */
  currency?: string
}

export function DocumentPreview({
  record,
  lines,
  entityName,
  currency,
}: DocumentPreviewProps) {
  const { t } = useTranslation()

  /* AC 8, derived during render rather than in an effect — the same pattern
     the form sheets' re-seed uses, and for the same reason: an effect
     would paint one frame of the stale answer first, and the stale answer here
     is "yesterday's PDF is current".

     What is remembered is the narrowest fact that answers the question: has a
     `documentId` ever been seen FOR THIS RECORD. Present → absent for the same
     id can only mean the draft was written to since it was rendered, because
     that write is the only thing that clears the column. The record id is part
     of the memory so that scrolling from a rendered document to a never-rendered
     one does not inherit the first one's history and accuse the second of being
     out of date. */
  const [seen, setSeen] = useState(() => ({
    recordId: record.id,
    hadRender: record.documentId !== undefined,
  }))
  let hadRender = seen.hadRender
  if (seen.recordId !== record.id) {
    hadRender = record.documentId !== undefined
    setSeen({ recordId: record.id, hadRender })
  } else if (record.documentId !== undefined && !hadRender) {
    hadRender = true
    setSeen({ recordId: record.id, hadRender: true })
  }
  const outOfDate = hadRender && record.documentId === undefined

  // Read into a local so the branch below narrows it: `record.documentId` is an
  // optional property, and testing a boolean derived from it leaves TypeScript
  // still believing the property could be undefined at the point it is passed on.
  const archived = record.documentId
  const rendered = archived !== undefined

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <header className="flex flex-col gap-1">
        <h3 className="asa-eyebrow">
          {rendered
            ? t('finance.documents.preview.renderedTitle')
            : t('finance.documents.preview.draftTitle')}
        </h3>
        {/* The hint is the DRAFT's alone. Saying "this is a layout of what has
            been typed, not the document" over the archived PDF would be the
            screen disowning the one artifact it can vouch for. */}
        {rendered ? null : (
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('finance.documents.preview.draftHint')}
          </p>
        )}
      </header>

      {/* Rides ABOVE the draft it explains, and only ever above a draft: an
          out-of-date record has no `documentId`, so there is no frame this could
          appear over. It is a banner rather than a replaced view because the
          layout below it is accurate — it is the ARCHIVE that is stale — and
          hiding the figures would withhold them exactly when somebody is
          checking what changed. */}
      {outOfDate ? (
        <Banner
          tone="attention"
          title={t('finance.documents.preview.outOfDate')}
        />
      ) : null}

      {archived !== undefined ? (
        /* Keyed by the archive row, so generating again mounts a FRESH viewer
           rather than reusing one holding the previous render's failure state.
           A `key` is the whole of that reset: tracking the id inside the viewer
           and clearing three pieces of state off it would be a hand-rolled
           remount that drifts the first time a fourth piece is added. */
        <RenderedDocument key={archived} documentId={archived} />
      ) : (
        <DraftDocument
          record={record}
          lines={lines}
          entityName={entityName}
          currency={currency}
        />
      )}
    </section>
  )
}

/** The archived PDF, behind a signed URL that is fetched and never kept.
 *
 *  ── Why the URL is not stored, linked or cached (00-decisions §2, AD-18) ───
 *
 *  The link is pre-signed and dies after five minutes, and it dies ON PURPOSE:
 *  AD-18 gives it that expiry precisely so a link copied out of a browser is
 *  worthless before it can be shared. Three consequences, all of them visible
 *  in the code below and none of them cosmetic:
 *
 *    · it goes on an <iframe src> and never on an <a href>. An anchor is a
 *      thing a person right-clicks and copies, and a copied credential is the
 *      one failure mode the expiry exists to bound — a five-minute link handed
 *      out through the UI is still a five-minute window in which anybody with
 *      the string reads a client's invoice, with no session and no audit trail;
 *
 *    · it is never lifted into component state. State outlives the frame it was
 *      drawn for, and a credential sitting in a closure after the viewer closed
 *      is a credential something else can read. The query owns it, `gcTime: 0`
 *      discards it on unmount, and this component only ever reads it;
 *
 *    · it is never cached for longer than the view. `useDocumentPreviewUrl`
 *      sets `staleTime: 0` for this reason, so the retry below is an ordinary
 *      `refetch()` that goes to the server and comes back with a URL good for
 *      another five minutes. A hook that held the answer for even the default
 *      thirty seconds would hand the retry back the SAME dead link and tell the
 *      reader the document is broken.
 *
 *  ── Two ways to fail, and both must reach the reader (AC 5) ────────────────
 *
 *  The fetch can fail, and the frame can fail. The second is the LIKELY one: an
 *  expired signature is rejected by object storage when the browser goes for
 *  the bytes, long after this client got a perfectly good 200 carrying the URL.
 *  A viewer that only watched the query would sit showing a blank frame and
 *  call it success. So `onError` on the <iframe> is wired as well, and either
 *  route lands on the same sentence and the same retry.
 *
 *  ── 404 is a different sentence entirely ──────────────────────────────────
 *
 *  `document` is a NIL-ABLE module: with no object storage configured `main.go`
 *  never constructs its service and `routes.go` never mounts its routes, so this
 *  endpoint answers 404 rather than 500. That means "there is no archive on this
 *  deployment", not "this document is missing" and certainly not "there is no
 *  paper" — an empty frame would say a case has no document when the truth is
 *  that there is no bucket to look in. Two different sentences, because they
 *  send the reader to two different people.
 *
 *  ── Exported, for one other caller ─────────────────────────────────────────
 *
 *  The case's read-only «Бумаги работы» list (`CaseArchiveList`) opens a status
 *  update or an offer through this same viewer: one owner of the signed-URL
 *  rules above, rather than a second frame that forgets one of them.
 */
export function RenderedDocument({
  documentId,
  unavailable,
}: {
  documentId: string
  /** What to say when the archive will not hand this document over — a 404 OR a
   *  403 (N-30). The issued-paper register lists rows a reader's role can see
   *  as figures but whose bytes may sit outside their case scope; the server
   *  answers 403/404 and the reader should be told "not available", quietly,
   *  not offered a Retry that cannot succeed. Omitted, a 404 keeps its own
   *  archive-not-configured sentence and a 403 stays a plain failure. */
  unavailable?: string
}) {
  const { t } = useTranslation()
  const preview = useDocumentPreviewUrl(documentId)

  /* A COUNTER rather than the failed URL, because the server is entitled to
     hand back a byte-identical signature for a second request inside the same
     second — and comparing URLs would then treat the fresh link as the one that
     already failed and refuse to try it. The counter is also the <iframe>'s
     key, so a retry replaces the element rather than asking a browser to reload
     a src it believes it already has. */
  const [attempt, setAttempt] = useState(0)
  const [failedAttempt, setFailedAttempt] = useState<number | null>(null)

  const url = preview.data?.url
  const failed = preview.isError || failedAttempt === attempt

  const retry = () => {
    setAttempt((current) => current + 1)
    setFailedAttempt(null)
    void preview.refetch()
  }

  /* A NATIVE listener through a ref, not React's `onError` prop, and this is
     load-bearing rather than fussy: React does not deliver `error` on an
     <iframe> at all. `error` and `load` do not bubble, so React attaches them
     directly to the element instead of to the root — and its own tag table
     (`setInitialProperties` in react-dom) registers only `load` for `iframe`,
     `object` and `embed`, reserving `error` for `img`, `image` and `source`.
     An `onError={…}` prop written here would type-check, read as wired, and
     never fire once: the reader would sit in front of a blank frame that the
     screen believes is fine, which is the exact failure AC 5 exists to prevent.

     `load` is not a substitute for it. Object storage refusing an expired
     signature answers with an error BODY, which the browser loads into the
     frame and reports as a successful load; and the frame is cross-origin, so
     nothing in it can be inspected from here. `error` is the only signal this
     side gets, so it is the one that is listened for.

     Keyed to `attempt` so the listener a retry installs reports against the
     attempt on screen, and cleaned up through the ref callback's return —
     React 19's own mechanism, rather than an effect that would have to
     re-derive which element it was watching. */
  const watchFrame = useCallback(
    (frame: HTMLIFrameElement | null) => {
      if (frame === null) return
      const onError = () => setFailedAttempt(attempt)
      frame.addEventListener('error', onError)
      return () => frame.removeEventListener('error', onError)
    },
    [attempt],
  )

  /* Asked before the generic failure, because it is not one: nothing here has
     gone wrong, the environment simply has no archive. `isNotFound` covers the
     unmounted module and a missing archive row alike — AD-28 makes the server
     answer both the same way on purpose, so that a caller guessing ids cannot
     tell them apart, and this screen has no more information than the caller. */
  if (
    unavailable !== undefined &&
    preview.error instanceof ApiError &&
    (preview.error.isNotFound || preview.error.isForbidden)
  ) {
    return (
      <EmptyState
        title={t('finance.documents.preview.renderedTitle')}
        description={unavailable}
      />
    )
  }

  if (preview.error instanceof ApiError && preview.error.isNotFound) {
    return (
      <EmptyState
        title={t('finance.documents.preview.renderedTitle')}
        description={t('finance.documents.archiveUnavailable')}
      />
    )
  }

  if (failed) {
    return (
      <Banner
        tone="attention"
        title={t('finance.documents.preview.failed')}
        action={
          <Button variant="secondary" size="sm" onClick={retry}>
            {t('common.actions.retry')}
          </Button>
        }
      />
    )
  }

  if (url === undefined) {
    return (
      <div
        className="flex flex-col gap-2"
        aria-busy="true"
        aria-label={t('finance.documents.preview.loading')}
      >
        <Skeleton className="h-[420px] w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  return (
    <iframe
      key={attempt}
      ref={watchFrame}
      src={url}
      /* Without an accessible name a screen reader announces an unnamed frame
         in the middle of the page and the reader has no way to know what it
         holds. */
      title={t('finance.documents.preview.frameTitle')}
      className="border-line bg-surface-sunken h-[420px] w-full rounded-[var(--radius-DEFAULT)] border"
    />
  )
}

/** The live layout, drawn from the fields this client already holds.
 *
 *  `useOfferPreview`'s precedent, copied in spirit rather than extended: the
 *  client lays out what it has, and the artifact is somebody else's job. The
 *  difference from that sheet is that the offer preview renders the SERVER's
 *  composition and this one does not — there is no compose-a-document endpoint
 *  for an invoice or a debit note, and asking for one would put a round trip
 *  back in the keystroke loop this mode exists to keep out of it.
 *
 *  It therefore does NOT try to reproduce the PDF's typography, and the gap is
 *  deliberate. This is a draft and it says so, twice: in the heading above it
 *  and in the word standing where the number will go. A layout that passed for
 *  the document would be a layout somebody screenshots and sends. */
function DraftDocument({
  record,
  lines,
  entityName,
  currency,
}: {
  record: CaseDocument
  lines: DocumentPreviewLine[]
  entityName?: string
  currency?: string
}) {
  const { t } = useTranslation()
  const format = useFormat()
  // The screen's answer first, the record's second. `CaseDocument.entityName`
  // is '' for both kinds — see DocumentPreviewProps — so this is normally the
  // caller's, and the fallback exists only so a caller that does have one on
  // the record is not overridden by an empty prop.
  const entity = entityName ?? (record.entityName === '' ? undefined : record.entityName)
  const money = currency ?? record.total.currency

  return (
    /* A page, not a form — bounded and on its own surface, so what is being
       checked reads as paper rather than as the screen that produced it. */
    <article className="border-line bg-surface flex flex-col gap-4 rounded-[var(--radius-DEFAULT)] border px-4 py-4">
      <header className="border-line flex flex-wrap items-start justify-between gap-3 border-b pb-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="asa-eyebrow">{t(`documentKind.${record.kind}`)}</span>
          {/* The number, or the word that stands where it will be. A draft has
              none: minting belongs to the issuing transaction (AD-12), and a
              plausible-looking placeholder is how a number reaches a client
              before it exists. The fixture printed the literal string "DRAFT"
              as the number for exactly this reason and it was wrong for exactly
              this reason — this is the label, not the value. */}
          <span className="text-text-heading text-[17px] leading-none font-semibold tracking-[-0.3px]">
            {record.number ?? t('finance.documents.preview.draftBadge')}
          </span>
          <span className="text-text-muted asa-num text-[12.5px] font-light">
            {record.caseNumber}
            <span aria-hidden className="text-text-faint px-1.5">
              ·
            </span>
            {/* Formatted in the reader's language, like every label around it.
                It used to print the wire's ISO "as served", on the theory that
                this sketch mirrors the document — but the PDF itself never
                prints ISO: `platform/docs` writes the date with the month in
                letters (`FormatDateIn`, «31 Oct 2026»), so the raw value
                matched neither the paper nor the screen. The draft layout does
                not reproduce the PDF's typography anyway (see the file
                header); the RENDERED preview is the place to read the exact
                printed form. Still date-only and zone-free (AD-19). */}
            {format.date(record.documentDate)}
          </span>
        </div>

        <div className="flex min-w-0 flex-col items-end gap-1 text-right">
          {entity ? (
            <span className="flex flex-col items-end gap-0.5">
              <span className="asa-eyebrow">{t('finance.documents.entity')}</span>
              <span className="text-text-body text-[12.5px]">{entity}</span>
            </span>
          ) : null}
          <span className="flex flex-col items-end gap-0.5">
            <span className="asa-eyebrow">
              {t('finance.documents.raise.counterparty')}
            </span>
            {/* AD-6: a debit note's recipient is withheld from a caller without
                agent-identity visibility, and the marker is rendered rather than
                a blank — a blank on a document reads as a field nobody filled
                in, which is somebody's cue to go "fix" it. The amounts below
                stay visible either way; only the identity is suppressed. */}
            <SuppressedValue
              value={record.counterpartyName}
              className="text-text-heading text-[13px]"
            />
          </span>
        </div>
      </header>

      {lines.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <h4 className="asa-eyebrow">{t('finance.documents.lines.label')}</h4>
            {/* The currency, stated once for the whole table — the rows carry
                bare figures, so dropping this leaves numbers with no currency
                anywhere on the page. */}
            <span className="text-text-muted text-[11px] font-light">{money}</span>
          </div>
          {/* Scrolls inside itself: four columns must not push the panel
              sideways when it sits beside a form. */}
          <div className="asa-scroll min-w-0 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-line border-b">
                  <th scope="col" className="asa-eyebrow h-8 px-2 pl-0">
                    {t('finance.documents.lines.description')}
                  </th>
                  <th scope="col" className="asa-eyebrow h-8 px-2 text-right">
                    {t('finance.documents.lines.quantity')}
                  </th>
                  <th scope="col" className="asa-eyebrow h-8 px-2 text-right">
                    {t('finance.documents.lines.unitPrice')}
                  </th>
                  {/* `amount`, not `total` — the row's figure is what this one
                      charge comes to, and heading it "Total" beside the
                      document's own total below is two different figures under
                      one word. */}
                  <th scope="col" className="asa-eyebrow h-8 px-2 pr-0 text-right">
                    {t('common.fields.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-line-hair border-b last:border-b-0">
                    <td className="px-2 py-1.5 pl-0 text-[12.5px]">
                      <span className="text-text-body block">{line.description}</span>
                      {line.note ? (
                        <span className="text-text-muted block text-[11.5px] leading-snug font-light">
                          {line.note}
                        </span>
                      ) : null}
                    </td>
                    {/* Each figure at the precision it was STORED with, not at
                        two places. A quantity is not money — it validates to six
                        places and detention is billed in part-days — so 1.375
                        shown as "1.38" beside an unchanged amount is a breakdown
                        that no longer multiplies to the figure printed next to
                        it, which is the one thing a reader checks a line for. */}
                    <td className="asa-num text-text-body px-2 py-1.5 text-right text-[12.5px]">
                      {figure(line.quantity)}
                    </td>
                    <td className="asa-num text-text-body px-2 py-1.5 text-right text-[12.5px]">
                      {figure(line.unitPrice)}
                    </td>
                    {/* SERVED, never multiplied here (AD-9) — see
                        DocumentPreviewLine.amount. */}
                    <td className="asa-num text-text-heading px-2 py-1.5 pr-0 text-right text-[12.5px]">
                      {formatNumber(line.amount, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* The total is the RECORD's, not a sum of the rows above it. The server
          computes it (AD-9) and it is the figure the ledger carries; a column
          summed on this side would be a second answer, and the two differ the
          moment VAT or a rounding rule lands on one of them and not the other. */}
      <dl className="border-line ml-auto flex w-full max-w-[280px] flex-col border-t pt-2">
        <div className="flex items-baseline justify-between gap-4 py-1">
          <dt className="text-text-heading text-[12.5px] font-medium">
            {t('finance.documents.total')}
          </dt>
          <dd className={cn('asa-num text-text-heading text-[13px] font-semibold')}>
            {formatNumber(record.total.amount, 2)}
            <span className="text-text-muted pl-1 text-[11.5px] font-light">
              {record.total.currency}
            </span>
          </dd>
        </div>
      </dl>
    </article>
  )
}

/** A non-money figure at the precision it was written with.
 *
 *  Quantities and unit prices are not rendered at a fixed two places for the
 *  reason the invoice line editor gives at length: they are allowed six, and
 *  padding or truncating either one produces a breakdown that stops multiplying
 *  to the amount beside it. An empty quantity prints as 1, because the row's
 *  unit price IS its amount and "× 100.00" with nothing in front of it reads as
 *  a figure the page lost rather than as a lump sum nobody broke down. */
function figure(raw: string): string {
  const value = raw === '' ? '1' : raw
  return formatNumber(value, decimalPlaces(value))
}
